/**
 * TodayZone — renders preview cards for doors marked `previewable: true`
 * in `doors.config.json`. Lives in the AWENI home, ABOVE the service grid,
 * BELOW the hero (per docs/MODULE-INTEGRATION.md).
 *
 * Rendering rules (Home Page Anti-Regression rules §1, §10):
 *   - Synchronous render uses last cached PreviewSnapshot from localStorage.
 *   - Background refresh dynamically imports each module + calls preview()
 *     inside a requestIdleCallback after first paint.
 *   - One module's failure NEVER breaks the home — try/catch around each.
 *   - Cards collapse gracefully to "Tap to open {label}" if cache empty AND
 *     preview() rejects.
 */

import type { AweniContext, Lang } from '../../types';
import type { PreviewSnapshot, PreviewableAweniModule } from '../types';
import { isPreviewable } from '../types';
import doorsConfig from './doors.config.json';
import { renderIcon } from './icons';
import { renderHolidayCard } from './widgets/HolidayCard';
import { createHijriCalendar } from '../../services/HijriCalendar.impl';
import type { Country, HijriMethod } from '../../services/HijriCalendar';

interface PreviewableDoor {
  id: string;
  labelKey: string;
  icon: string;
  color: string;
  route: string;
  category: string;
  tier: 'primary' | 'secondary';
  previewable?: boolean;
}

const SNAPSHOT_CACHE_PREFIX = 'aweni_preview_';

function readCachedSnapshot(moduleId: string): PreviewSnapshot | null {
  try {
    const raw = localStorage.getItem(SNAPSHOT_CACHE_PREFIX + moduleId);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PreviewSnapshot;
    return parsed;
  } catch {
    return null;
  }
}

function writeCachedSnapshot(moduleId: string, snap: PreviewSnapshot): void {
  try {
    localStorage.setItem(SNAPSHOT_CACHE_PREFIX + moduleId, JSON.stringify(snap));
  } catch { /* quota — ignore */ }
}

function escHtml(s: string): string {
  return String(s).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]!));
}

function renderCard(door: PreviewableDoor, snap: PreviewSnapshot | null, t: (k: string) => string): string {
  const fallbackLabel = t(door.labelKey) || door.id;
  if (!snap) {
    // No cache yet — render a graceful placeholder. ariaLabel is the
    // door label only since we don't have metric data yet.
    return `
      <a class="aweni-today-card aweni-today-card--placeholder"
         data-door-id="${escHtml(door.id)}"
         href="#${door.route.startsWith('/') ? door.route : '/' + door.route}"
         aria-label="${escHtml(fallbackLabel)}">
        <span class="aweni-today-card__icon-wrap" style="background:${escHtml(door.color)}1A;color:${escHtml(door.color)};">
          ${renderIcon(door.icon, 22)}
        </span>
        <div class="aweni-today-card__body">
          <div class="aweni-today-card__title">${escHtml(fallbackLabel)}</div>
          <div class="aweni-today-card__placeholder">${escHtml(t('preview_tap_to_open') || 'Tap to open')}</div>
        </div>
      </a>
    `;
  }
  const variant = snap.bgVariant || 'neutral';
  const ariaLabel = snap.ariaLabel || fallbackLabel;
  const ctaRoute = snap.ctaRoute.startsWith('#') ? snap.ctaRoute : '#' + snap.ctaRoute;
  const errorMsg = snap.errorState?.message;
  return `
    <a class="aweni-today-card aweni-today-card--${escHtml(variant)}"
       data-door-id="${escHtml(door.id)}"
       href="${escHtml(ctaRoute)}"
       aria-label="${escHtml(ariaLabel)}">
      <span class="aweni-today-card__icon-wrap" style="background:${escHtml(door.color)}1A;color:${escHtml(door.color)};">
        ${renderIcon(snap.iconName || door.icon, 22)}
      </span>
      <div class="aweni-today-card__body">
        <div class="aweni-today-card__title">${escHtml(fallbackLabel)}</div>
        ${errorMsg ? `
          <div class="aweni-today-card__placeholder">${escHtml(errorMsg)}</div>
        ` : `
          <div class="aweni-today-card__primary">
            ${snap.primaryMetric.label ? `<span class="aweni-today-card__metric-label">${escHtml(snap.primaryMetric.label)}</span>` : ''}
            <span class="aweni-today-card__metric-value">${escHtml(snap.primaryMetric.value)}</span>
          </div>
          ${snap.secondaryMetric ? `
            <div class="aweni-today-card__secondary">
              ${snap.secondaryMetric.label ? `<span class="aweni-today-card__metric-label">${escHtml(snap.secondaryMetric.label)}</span>` : ''}
              <span>${escHtml(snap.secondaryMetric.value)}</span>
            </div>
          ` : ''}
        `}
      </div>
    </a>
  `;
}

const STYLES_INJECTED_FLAG = '__aweni_today_zone_css__';

function injectStyles(): void {
  if ((window as unknown as Record<string, unknown>)[STYLES_INJECTED_FLAG]) return;
  (window as unknown as Record<string, unknown>)[STYLES_INJECTED_FLAG] = true;
  const style = document.createElement('style');
  style.textContent = `
    .home-today-cards-container {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
      gap: 12px;
      margin: 12px 0 16px 0;
    }
    /* HolidayCard banner — rendered above today cards. */
    .home-today-holiday-banner {
      grid-column: 1 / -1;
      margin: 0 0 4px 0;
    }
    .ln-holiday-card {
      background: var(--ln-bg-card, #fff);
      border: 1px solid var(--ln-accent-ceremonial, #C8A96A);
      border-radius: var(--ln-radius-lg, 16px);
      padding: 14px 16px;
      box-shadow: var(--ln-shadow-sm, 0 1px 3px rgba(0,0,0,0.06));
      color: var(--ln-text-primary, #0A0A0A);
    }
    .ln-holiday-card[data-pattern="C_confirmed"] {
      border-color: var(--ln-accent, #0A0A0A);
    }
    .ln-holiday-card__header {
      display: flex; align-items: center; gap: 10px; margin: 0 0 6px 0;
    }
    .ln-holiday-card__header h3 {
      font-size: 1.05rem; font-weight: 700; margin: 0;
      color: var(--ln-text-primary, #0A0A0A);
    }
    .ln-holiday-icon { font-size: 1.4rem; line-height: 1; }
    .ln-countdown {
      font-weight: 600; color: var(--ln-text-secondary, #555);
      font-size: 0.9rem;
    }
    .ln-countdown-imminent {
      color: var(--ln-accent-ceremonial, #C8A96A); text-transform: uppercase;
    }
    .ln-holiday-hijri {
      font-size: 0.85rem; color: var(--ln-text-secondary, #666);
      margin: 0 0 4px 0;
    }
    .ln-holiday-gregorian-approx {
      font-size: 1rem; font-weight: 600; margin: 0 0 6px 0;
      display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
    }
    .ln-uncertainty-badge {
      display: inline-block; padding: 2px 8px; border-radius: 999px;
      background: var(--ln-accent-ceremonial, #C8A96A);
      color: #fff; font-size: 0.72rem; font-weight: 700; letter-spacing: 0.04em;
      text-transform: uppercase;
    }
    .ln-sighting-note {
      font-size: 0.82rem; color: var(--ln-text-secondary, #666); margin: 0;
      font-style: italic;
    }
    .ln-uncertainty-dates {
      display: grid; grid-template-columns: auto 1fr;
      gap: 2px 12px; margin: 6px 0; font-size: 0.92rem;
    }
    .ln-uncertainty-dates dt {
      color: var(--ln-text-secondary, #666); font-weight: 500;
    }
    .ln-uncertainty-dates dd {
      margin: 0; font-weight: 600;
    }
    .ln-divider {
      border: 0; border-top: 1px solid var(--ln-border, rgba(0,0,0,0.06));
      margin: 8px 0;
    }
    .ln-country-hint {
      font-size: 0.85rem; color: var(--ln-text-secondary, #555);
      margin: 0 0 8px 0;
    }
    .ln-confirmed-dates {
      list-style: none; padding: 0; margin: 6px 0 8px 0;
      font-size: 0.92rem; line-height: 1.5;
    }
    .ln-confirmed-dates li {
      display: flex; align-items: center; gap: 6px;
      margin-bottom: 2px;
    }
    .ln-checkmark { font-size: 0.95rem; }
    .ln-countries { color: var(--ln-text-secondary, #555); }
    .ln-your-country {
      font-size: 0.92rem; font-weight: 600;
      margin: 6px 0 0 0;
      color: var(--ln-accent, #0A0A0A);
    }
    .ln-btn {
      display: inline-block; padding: 8px 14px;
      border-radius: var(--ln-radius-md, 12px);
      font-weight: 600; font-size: 0.88rem;
      cursor: pointer; border: 1px solid transparent;
      background: var(--ln-bg-card, #fff);
      color: var(--ln-text-primary, #0A0A0A);
      text-decoration: none;
    }
    .ln-btn-secondary {
      border-color: var(--ln-border, rgba(0,0,0,0.12));
    }
    .ln-btn-secondary:hover {
      background: var(--ln-accent, #0A0A0A);
      color: var(--ln-accent-text, #fff);
      border-color: var(--ln-accent, #0A0A0A);
    }
    [dir="rtl"] .ln-holiday-card__header { direction: rtl; }
    [dir="rtl"] .ln-uncertainty-dates { direction: rtl; }
    .aweni-today-card {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px 14px;
      border-radius: var(--ln-radius-lg, 16px);
      background: var(--ln-bg-card, #fff);
      border: 1px solid var(--ln-border, rgba(0,0,0,0.06));
      box-shadow: var(--ln-shadow-sm, 0 1px 3px rgba(0,0,0,0.04));
      text-decoration: none;
      color: inherit;
      transition: transform 120ms ease, box-shadow 120ms ease;
      min-height: 72px;
    }
    .aweni-today-card:hover {
      transform: translateY(-1px);
      box-shadow: var(--ln-shadow-md, 0 2px 8px rgba(0,0,0,0.06));
    }
    .aweni-today-card:focus-visible {
      outline: 2px solid var(--ln-accent, #0A0A0A);
      outline-offset: 2px;
    }
    .aweni-today-card--success { background: var(--ln-bg-success, var(--ln-bg-card, #fff)); }
    .aweni-today-card--attention {
      background: var(--ln-bg-attention, var(--ln-bg-card, #fff));
      border-color: var(--ln-accent-ceremonial, #C8A96A);
    }
    .aweni-today-card__icon-wrap {
      flex: 0 0 auto;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 44px;
      height: 44px;
      border-radius: 12px;
    }
    .aweni-today-card__body { min-width: 0; flex: 1; }
    .aweni-today-card__title {
      font-size: 0.82rem;
      font-weight: 600;
      color: var(--ln-text-secondary, #555);
      text-transform: uppercase;
      letter-spacing: 0.04em;
      margin-bottom: 2px;
    }
    .aweni-today-card__primary {
      font-size: 1.05rem;
      font-weight: 600;
      color: var(--ln-text-primary, #0A0A0A);
      line-height: 1.25;
      display: flex;
      gap: 6px;
      align-items: baseline;
      flex-wrap: wrap;
    }
    .aweni-today-card__metric-label {
      font-size: 0.85rem;
      color: var(--ln-text-secondary, #666);
      font-weight: 500;
    }
    .aweni-today-card__metric-value { font-weight: 700; }
    .aweni-today-card__secondary {
      font-size: 0.85rem;
      color: var(--ln-text-secondary, #666);
      margin-top: 2px;
      line-height: 1.3;
    }
    .aweni-today-card__placeholder {
      font-size: 0.95rem;
      color: var(--ln-text-secondary, #666);
    }
    [dir="rtl"] .aweni-today-card { text-align: right; }
  `;
  document.head.appendChild(style);
}

const MODULE_LOADERS: Record<string, () => Promise<{ default: PreviewableAweniModule } | unknown>> = {
  memoriz: () => import('../hisni/HisniModule'),
  yawmi: () => import('../yawmi/YawmiModule'),
};

/**
 * Read user country preference (set by Settings or auto-derived from city).
 * Defaults to 'global' so the holiday card shows Saudi (Umm al-Qura) primary
 * dates without country-specific overrides — better than no card at all.
 */
function readUserCountry(): Country {
  try {
    const raw = localStorage.getItem('aweni_pref_country');
    if (raw && /^[A-Z]{2}$/.test(raw)) return raw as Country;
  } catch { /* ignore */ }
  return 'global';
}

/**
 * Render the holiday banner ABOVE the today cards if there's an upcoming
 * Islamic holiday. v1 always shows the next holiday (no day-cap) so users
 * see Eid/Ramadan months in advance — gives the calendar door a reason to
 * exist on the home. Failure is silent (banner just doesn't render).
 */
function renderHolidayBanner(ctx: AweniContext): string {
  try {
    const country = readUserCountry();
    const cal = createHijriCalendar({
      country,
      method: 'sighting_local' as HijriMethod,
      hijriOffset: 0,
      locale: ctx.i18n.locale,
      includeNationalHolidays: false,
      includeAstronomical: false,
    });
    const next = cal.getNextHoliday();
    if (!next) return '';
    const html = renderHolidayCard({ result: next, ctx, country: String(country) });
    return `<div class="home-today-holiday-banner">${html}</div>`;
  } catch {
    return '';
  }
}

/**
 * Mount the Today zone into the given container. Synchronous render with
 * cached snapshots; background refresh after first paint.
 */
export function mountTodayZone(container: HTMLElement, ctx: AweniContext): void {
  injectStyles();

  const t = (k: string) => ctx.i18n.t(k);
  const lang: Lang = ctx.i18n.locale;

  const previewableDoors = (doorsConfig.doors as PreviewableDoor[])
    .filter((d) => d.previewable === true);

  // Holiday banner — synchronously computed from pre-baked catalog (no I/O).
  const holidayBanner = renderHolidayBanner(ctx);

  if (previewableDoors.length === 0 && !holidayBanner) {
    // Nothing to render — leave container empty.
    container.innerHTML = '';
    return;
  }

  // Synchronous first paint — cached snapshots only + optional holiday banner.
  container.innerHTML = holidayBanner + previewableDoors
    .map((door) => renderCard(door, readCachedSnapshot(door.id), t))
    .join('');

  // Background refresh after first paint. Per home anti-regression rule:
  // never block first paint on third-party API calls.
  const idleCb = (): void => {
    void Promise.allSettled(
      previewableDoors.map(async (door) => {
        const loader = MODULE_LOADERS[door.id];
        if (!loader) return;
        try {
          const mod = await loader();
          const candidate = (mod as { default?: unknown }).default ?? mod;
          if (!isPreviewable(candidate)) return;
          const fresh = await Promise.race<PreviewSnapshot>([
            candidate.preview(ctx),
            new Promise<PreviewSnapshot>((_resolve, reject) =>
              setTimeout(() => reject(new Error('preview-timeout')), 250),
            ),
          ]);
          writeCachedSnapshot(door.id, fresh);
          // Re-render only this card if still attached
          const cardEl = container.querySelector<HTMLElement>(
            `.aweni-today-card[data-door-id="${door.id}"]`,
          );
          if (cardEl) {
            const fragment = document.createElement('div');
            fragment.innerHTML = renderCard(door, fresh, t);
            const newEl = fragment.firstElementChild as HTMLElement | null;
            if (newEl) cardEl.replaceWith(newEl);
          }
        } catch {
          // Card stays on cached snapshot or placeholder — never breaks home.
        }
      }),
    );
  };

  if ('requestIdleCallback' in window) {
    (window as unknown as { requestIdleCallback: (cb: () => void) => number }).requestIdleCallback(idleCb);
  } else {
    setTimeout(idleCb, 50);
  }

  // Mark lang on container for RTL-aware CSS
  container.setAttribute('dir', ctx.i18n.dir);
  container.setAttribute('data-lang', lang);
}
